import { useEffect } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ResponsiveFormHost } from "../components/shared/ResponsiveFormHost";
import { ExpenseForm } from "../components/expenses/ExpenseForm";
import { ExpenseFormProvider } from "../context/ExpenseFormContext";
import { NotFoundPage } from "./NotFoundPage";
import { useEvents } from "../context/EventContext";
import { useIsMobile } from "../hooks/useIsMobile";

export function CreateExpensePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const {
    setCurrentEventId,
    currentEvent,
    isLoading,
    events,
    addExpense,
  } = useEvents();

  useEffect(() => {
    if (eventId) {
      setCurrentEventId(eventId);
    }
    return () => setCurrentEventId(null);
  }, [eventId, setCurrentEventId]);

  const parentRoute = `/event/${eventId}/expenses`;
  const wizardRoot = `/event/${eventId}/expenses/new`;

  // Show 404 if event not found (after loading)
  if (!isLoading && eventId && !events.find((e) => e.id === eventId)) {
    return <NotFoundPage />;
  }

  if (isLoading || !currentEvent) {
    return null;
  }

  const { participants, currency } = currentEvent;

  // Mobile: nested routes drive the wizard. If we're at /new (no step), redirect to step-1.
  if (isMobile) {
    if (location.pathname === wizardRoot) {
      return <Navigate to="step-1" replace />;
    }

    return (
      <ExpenseFormProvider>
        <Outlet />
      </ExpenseFormProvider>
    );
  }

  // Desktop: render classic ExpenseForm inside Modal via ResponsiveFormHost.
  const handleSubmit = (
    description: string,
    amount: number,
    paidById: string,
    participantIds: string[]
  ) => {
    addExpense(description, amount, paidById, participantIds);
    navigate(parentRoute);
  };

  return (
    <ResponsiveFormHost title="Nuevo gasto" parentRoute={parentRoute}>
      <ExpenseForm
        participants={participants}
        currency={currency}
        onSubmit={handleSubmit}
        onCancel={() => navigate(parentRoute)}
      />
    </ResponsiveFormHost>
  );
}
